import type { TDSRate, TdsSectionType } from "../../../../services/api";

/**
 * Shared shape for the TDS and TCS tables.
 *
 * Both are laid out on one grid template so the two panels read as one screen
 * rather than two components that happen to sit above each other. Following
 * master-admin's onboarding table: fixed columns for the things that are short
 * and uniform, the flexible share to the column a CA actually reads down.
 *
 * Section is fixed rather than flexible because its content is not uniform — the
 * Act 1961 codes are short ("194H") but the Income Tax Act 2025 renamed them to
 * descriptive keys, so "Payment to non union employee" is also a section.
 */
export const TDS_COLUMNS =
  "sm:grid-cols-[9rem_minmax(0,2fr)_4rem_5.5rem_3.5rem]";

/** Zoho's cap on a tax name, enforced where somebody can still act on it. */
export const TAX_NAME_MAX = 50;

/**
 * 1 April of the financial year `on` falls in. Zoho wants a start date and a CA
 * setting up a rate almost always means "from this FY", so that is the default
 * rather than today.
 */
export const financialYearStart = (on: Date): string => {
  const year = on.getMonth() >= 3 ? on.getFullYear() : on.getFullYear() - 1;
  return `${year}-04-01`;
};

export const TCS_COLUMNS =
  "sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_4rem_5.5rem]";

/**
 * TCS_COLUMNS with the action column the TDS table has, for the list that offers
 * a Zoho edit per row. Same widths so the two panels still read as one table
 * language; 3.5rem is what TDS_COLUMNS gives the word "Edit".
 */
export const TCS_COLUMNS_EDITABLE =
  "sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_4rem_5.5rem_3.5rem]";

/**
 * Whether a row is expired, from a value that may be 1, "1", true or absent.
 *
 * The TCS list endpoint returns the raw column, so `is_expired` arrives as the
 * STRING "1" or "0" — and "0" is truthy in JavaScript, which would paint every
 * rate Expired. Absent (a row from before the column existed) counts as expired,
 * matching the column's own default: a rate whose era is unknown should not be
 * offered as current.
 */
export const isExpiredFlag = (value: unknown): boolean => {
  if (value === undefined || value === null || value === "") return true;
  return Number(value) === 1;
};

/**
 * A section label short and code-like enough to be worth setting in mono.
 *
 * "194H" is an identifier and reads as one. "Commission or brokerage" is a
 * sentence, and mono costs it width for nothing.
 */
export const isCodeLike = (label: string): boolean => /^[\d(]/.test(label);

/** Rates the picker can still offer first, then the rest; each group by name. */
export const byEraThenName = (a: TDSRate, b: TDSRate): number => {
  if (a.is_expired !== b.is_expired) return a.is_expired ? 1 : -1;
  return a.tds_tax_name.localeCompare(b.tds_tax_name);
};

/**
 * The Act 1961 number the 2025 section replaced, when the catalogue knows it.
 * A CA thinks in these, so the new key alone is not enough to check a rate by.
 */
export const oldSectionFor = (
  rate: TDSRate,
  sections: Map<string, TdsSectionType>,
): string | null => {
  if (!rate.tax_specific_type) return null;
  return sections.get(rate.tax_specific_type)?.old_section_no || null;
};

/**
 * What goes in the Section column.
 *
 * The curated `section` wins, because it is the field the Edit dialog offers.
 * Preferring the catalogue over it would make every edit of that box invisible
 * on the twenty-two synced rows, whose keys the catalogue all knows — an
 * editable field whose edits do not show is worse than a label that is
 * occasionally out of date.
 *
 * The catalogue's `section_no` is next, and it is what a synced rate gets:
 * those arrive with `section` empty and only the raw key set, and
 * "Section 393(1) Sl1(ii)" is what Zoho's own rates list prints, so the column
 * reads the same as the screen a CA checks it against. Printing
 * `income_other_interest_on_securities` instead is neither readable nor what
 * anything else calls it.
 *
 * Last resort humanises the key, so an unknown section still reads as words.
 * Either way the `was 194H` sub-line carries the cross-reference.
 */
/**
 * TDS_COLUMNS with the Payable ledger column master-admin's review step carries.
 *
 * The room comes off Rate and Status, which hold "10%" and "Expired" and had
 * more than either needs — master's own reasoning, and the same arithmetic here.
 * The nature of payment keeps the larger flexible share because it is the column
 * a CA reads down; the ledger beside it is "TDS Payable" in almost every row, and
 * its floor stops it collapsing on the rare long account name.
 *
 * The old constant stays exactly where it was, unused by the new tables but still
 * exported — removing it is a separate decision from adding this.
 */
export const TDS_COLUMNS_LEDGER =
  "sm:grid-cols-[9rem_minmax(0,2fr)_3.5rem_minmax(5.5rem,1fr)_5rem_2rem]";

/** TCS_COLUMNS_EDITABLE's twin, with the same ledger column and the same widths. */
export const TCS_COLUMNS_LEDGER =
  "sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_3.5rem_minmax(5.5rem,1fr)_5rem_2rem]";

/**
 * The row just written, marked for the one glance that looks for it.
 *
 * master-admin tints it with `bg-primary/5 ring-1 ring-primary/30`. THE TENANT'S
 * BRAND RAMP IS GREYSCALE (brand-600 is rgb(38,38,38)), so a tinted version of
 * the accent would be an indistinguishable grey wash — the ring does the work
 * here and the fill only lifts the row off the page.
 */
export const FRESH_ROW_CLASS =
  "rounded-md bg-neutral-100 ring-1 ring-neutral-300";

export const sectionLabelFor = (
  rate: TDSRate,
  sections: Map<string, TdsSectionType>,
): string => {
  if (rate.section) return rate.section;
  const known = rate.tax_specific_type
    ? sections.get(rate.tax_specific_type)
    : undefined;
  if (known?.section_no) return known.section_no;
  if (rate.tax_specific_type) return rate.tax_specific_type.replace(/_/g, " ");
  return "—";
};
